import { FPNDetailsHierarchy } from 'src/types/productLines/productHierarchy.types';

/**
 * Result of aggregating FSD (First Ship Date) data from FPN children to RPN level
 */
export interface FSDAggregate {
  first_street_date: string | null;
  fsd_countries: string[] | null;
}

/**
 * Aggregates FSD (First Ship Date) data from FPN children to RPN level
 *
 * Business rules:
 * - first_street_date: Minimum (earliest) date among all non-null FPN dates
 * - fsd_countries: Distinct countries merged from all FPN children
 *
 * Note: This function expects FPN data that has already been parsed by the Redux slice.
 * The fsd_countries field should be a string[] | null, not a JSON string.
 *
 * @param fpnChildren - Array of FPN details (with pre-parsed countries arrays) from which to aggregate FSD data
 * @returns Aggregated FSD data with minimum date and distinct countries
 *
 * @example
 * ```typescript
 * const fpns = [
 *   { fsd_countries: ["US"], first_street_date: "2024-10-08", ... },
 *   { fsd_countries: ["CA", "UK"], first_street_date: "2024-09-15", ... },
 *   { fsd_countries: null, first_street_date: null, ... }
 * ];
 *
 * const result = aggregateFSDFromChildren(fpns);
 * // Result: {
 * //   first_street_date: "2024-09-15", // Minimum date
 * //   fsd_countries: ["CA", "UK", "US"] // Distinct, sorted
 * // }
 * ```
 */
export const aggregateFSDFromChildren = (fpnChildren: FPNDetailsHierarchy[]): FSDAggregate => {
  if (!fpnChildren || fpnChildren.length === 0) {
    return {
      first_street_date: null,
      fsd_countries: null,
    };
  }

  // Aggregate dates - find minimum (earliest) non-null date
  const validDates = fpnChildren.map((fpn) => fpn.first_street_date).filter((date): date is string => date !== null);

  const minDate = validDates.length > 0 ? validDates.sort()[0] : null;

  // Aggregate countries - collect all distinct countries
  // Note: Countries are already parsed as arrays by the Redux slice
  const allCountries = new Set<string>();

  fpnChildren.forEach((fpn) => {
    const countries = fpn.fsd_countries;
    if (countries && Array.isArray(countries)) {
      countries.forEach((country) => allCountries.add(country));
    }
  });

  const distinctCountries = allCountries.size > 0 ? Array.from(allCountries).sort() : null;

  return {
    first_street_date: minDate,
    fsd_countries: distinctCountries,
  };
};
